import { DollarSign, Percent, Plus, Trash2 } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";
import { IPlanoModalProps } from "@/pages/clients/clients.types";
import { EProductType } from "@/pages/products/products.type";
import { formatPrice } from "@/utils/monetary-formatter";

// Componente da modal de plano
export const PlanoModal: React.FC<IPlanoModalProps> = ({
  open,
  onOpenChange,
  tempPlano,
  setTempPlano,
  selectedProducts,
  editingPlanoIndex,
  onSave,
  projectType,
  projectPartners,
}) => {
  const isEditing = editingPlanoIndex !== undefined;
  const { t } = useI18n();

  // Estado para controlar erros de validação
  const [validationErrors, setValidationErrors] = React.useState<{
    planFeatures?: string;
    assistancePlanId?: string;
  }>({});

  // Cálculos de markup centralizado
  const totalMarkupPercentage =
    tempPlano.markup_mode === "percentage" ? tempPlano.markup || 0 : 0;
  const totalMarkupReal =
    tempPlano.markup_mode === "real_currency" ? tempPlano.markup || 0 : 0;

  // Função para validar o formulário
  const validateForm = () => {
    const errors: { planFeatures?: string; assistancePlanId?: string } = {};

    // Validar características do plano
    const validFeatures = tempPlano.plan_features.filter(
      (feature) => feature.trim() !== ""
    );

    if (validFeatures.length === 0) {
      errors.planFeatures = "Pelo menos uma característica é obrigatória";
    }

    // Validar ID do Plano para assistências
    const assistanceProducts = tempPlano.products.filter(
      (product) => {
        const originalProduct = selectedProducts.find((p) => p.id === product.id);
        return originalProduct?.type === EProductType.INSURANCE_ASSISTANCE;
      }
    );

    const assistanceWithoutPlanId = assistanceProducts.filter(
      (product) => !product.insurance_policy_extra_price_id || product.insurance_policy_extra_price_id.trim() === ""
    );

    if (assistanceWithoutPlanId.length > 0) {
      errors.assistancePlanId = "ID do Plano é obrigatório para Assistências";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return; // Não salva se houver erros de validação
    }

    onSave();
    onOpenChange(false);
  };

  const handleCancel = () => {
    setValidationErrors({}); // Limpa erros ao cancelar
    onOpenChange(false);
  };

  // Função para limpar erro quando características são modificadas
  const clearPlanFeaturesError = () => {
    if (validationErrors.planFeatures) {
      setValidationErrors((prev) => ({ ...prev, planFeatures: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[75vw] !max-w-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Plano" : "Adicionar Novo Plano"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Campos básicos do plano */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel>Nome do Plano</FormLabel>
              <Input
                value={tempPlano.name}
                onChange={(e) =>
                  setTempPlano((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome do plano"
              />
            </div>

            <div>
              <FormLabel>Descrição</FormLabel>
              <Input
                value={tempPlano.description}
                onChange={(e) =>
                  setTempPlano((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Descrição do plano"
              />
            </div>
          </div>

          {/* Características do plano */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                Características do Plano
                <span className="text-red-500 ml-1">*</span>
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (tempPlano.plan_features.length < 10) {
                    setTempPlano((prev) => ({
                      ...prev,
                      plan_features: [...prev.plan_features, ""],
                    }));
                    clearPlanFeaturesError();
                  }
                }}
                disabled={tempPlano.plan_features.length >= 10}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar característica
              </Button>
            </div>

            <div className="space-y-3">
              {tempPlano.plan_features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => {
                      setTempPlano((prev) => ({
                        ...prev,
                        plan_features: prev.plan_features.map((f, i) =>
                          i === index ? e.target.value : f
                        ),
                      }));
                      clearPlanFeaturesError();
                    }}
                    placeholder={`Característica ${index + 1}`}
                    className={`flex-1 ${
                      validationErrors.planFeatures ? "border-red-500" : ""
                    }`}
                  />
                  {tempPlano.plan_features.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTempPlano((prev) => ({
                          ...prev,
                          plan_features: prev.plan_features.filter(
                            (_, i) => i !== index
                          ),
                        }));
                        clearPlanFeaturesError();
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {tempPlano.plan_features.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma característica adicionada. Clique em "Adicionar
                característica" para começar.
              </p>
            )}

            {tempPlano.plan_features.length >= 10 && (
              <p className="text-sm text-muted-foreground text-center">
                Máximo de 10 características permitidas.
              </p>
            )}

            {validationErrors.planFeatures && (
              <p className="text-sm text-red-500 text-center">
                {validationErrors.planFeatures}
              </p>
            )}
          </div>

          {/* Comissões */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Carregamento comercial</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTempPlano((prev) => ({
                    ...prev,
                    commission_distribution: [
                      ...prev.commission_distribution,
                      {
                        agency: 0,
                        commission: 0,
                        position: prev.commission_distribution.length,
                      },
                    ],
                  }));
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar carregamento
              </Button>
            </div>

            {(() => {
              const commissionDistribution = tempPlano.commission_distribution;
              const totalCommissionOthers = Number(
                tempPlano.commission_lifetime_other_installments || 0
              );
              const totalCommission = totalCommissionOthers;

              const totalAgency = commissionDistribution.reduce(
                (sum: number, item: { agency: number; commission: number }) =>
                  sum + (Number(item.agency) || 0),
                0
              );

              return (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <Card className="p-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Total Agênciamento
                    </div>
                    <div className="text-2xl font-bold">
                      {totalAgency.toFixed(2)}%
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Mark-up %
                    </div>
                    <div className="text-2xl font-bold">
                      {(totalMarkupPercentage / 100)
                        .toFixed(2)
                        .replace(".", ",")}
                      %
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Mark-up R$
                    </div>
                    <div className="text-2xl font-bold">
                      R${" "}
                      {(totalMarkupReal / 100).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Total Comissão
                    </div>
                    <div className="text-2xl font-bold">
                      {totalCommission.toFixed(2)}%
                    </div>
                  </Card>
                </div>
              );
            })()}

            {/* Campos de comissão */}
            <div className="grid grid-cols-12 gap-4">
              {tempPlano.commission_distribution.map(
                (_, commissionIndex: number) => (
                  <Card key={commissionIndex} className="p-4 col-span-9">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-medium">
                        Carregamento comercial {commissionIndex + 1}º
                      </h5>
                      {tempPlano.commission_distribution.length > 1 ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (tempPlano.commission_distribution.length > 1) {
                              const newDistribution =
                                tempPlano.commission_distribution
                                  .filter(
                                    (_, i: number) => i !== commissionIndex
                                  )
                                  .map((item, index) => ({
                                    ...item,
                                    position: index,
                                  }));
                              setTempPlano((prev) => ({
                                ...prev,
                                commission_distribution: newDistribution,
                              }));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <FormLabel>Agênciamento (%)</FormLabel>
                        <Input
                          value={
                            tempPlano.commission_distribution[commissionIndex]
                              ?.agency || 0
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue =
                              rawValue === "" ? 0 : Number(rawValue);
                            // Limitar entre 0 e 99
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            // Limitar a 2 casas decimais
                            const roundedValue =
                              Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              commission_distribution:
                                prev.commission_distribution.map((item, i) =>
                                  i === commissionIndex
                                    ? { ...item, agency: roundedValue }
                                    : item
                                ),
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <FormLabel>Comissão (%)</FormLabel>
                        <Input
                          value={
                            tempPlano.commission_distribution[commissionIndex]
                              ?.commission || 0
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue =
                              rawValue === "" ? 0 : Number(rawValue);
                            // Limitar entre 0 e 99
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            // Limitar a 2 casas decimais
                            const roundedValue =
                              Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              commission_distribution:
                                prev.commission_distribution.map((item, i) =>
                                  i === commissionIndex
                                    ? { ...item, commission: roundedValue }
                                    : item
                                ),
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </Card>
                )
              )}

              {tempPlano.commission_distribution.length > 0 && (
                <Card className="p-4 col-span-3 self-start h-full">
                  <CardTitle className="text-sm font-medium mb-4">
                    Demais
                  </CardTitle>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <FormLabel>
                          {tempPlano.markup_mode === "real_currency"
                            ? "Mark-up (R$)"
                            : "Mark-up (%)"}
                        </FormLabel>
                        <div className="flex items-center gap-2">
                          <Percent className="w-3 h-3 text-muted-foreground" />
                          <Switch
                            checked={tempPlano.markup_mode === "real_currency"}
                            onCheckedChange={(checked) => {
                              setTempPlano((prev) => ({
                                ...prev,
                                markup_mode: checked
                                  ? ("real_currency" as const)
                                  : ("percentage" as const),
                              }));
                            }}
                          />
                          <DollarSign className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </div>

                      {tempPlano.markup_mode === "real_currency" ? (
                        <RealCurrencyInput
                          value={tempPlano.markup || 0}
                          onChange={(value) => {
                            setTempPlano((prev) => ({
                              ...prev,
                              markup: value,
                            }));
                          }}
                        />
                      ) : (
                        <Input
                          value={tempPlano.markup ? tempPlano.markup / 100 : 0}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue =
                              rawValue === "" ? 0 : Number(rawValue);
                            // Limitar entre 0 e 99
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            // Limitar a 2 casas decimais
                            const roundedValue =
                              Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              markup: roundedValue * 100,
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          placeholder="0.00"
                        />
                      )}
                    </div>

                    <div>
                      <FormLabel>Comissão (%)</FormLabel>
                      <Input
                        value={
                          tempPlano.commission_lifetime_other_installments || 0
                        }
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          let newValue = rawValue === "" ? 0 : Number(rawValue);
                          // Limitar entre 0 e 99
                          if (newValue > 99) newValue = 99;
                          if (newValue < 0) newValue = 0;
                          // Limitar a 2 casas decimais
                          const roundedValue = Math.round(newValue * 100) / 100;
                          setTempPlano((prev) => ({
                            ...prev,
                            commission_lifetime_other_installments:
                              roundedValue,
                          }));
                        }}
                        type="number"
                        step="0.01"
                        min="0"
                        max="99"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* Informação para projetos de afiliados */}
          {projectType === "affiliate" && (
            <div className="space-y-4">
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      Projeto de Afiliados
                    </h4>
                    <p className="text-sm text-blue-700">
                      Para projetos do tipo afiliados, os parceiros serão cadastrados posteriormente. 
                      Configure apenas os níveis e percentuais de comissão na seção de Carregamento Comercial acima.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Seleção de Parceiros por Nível (apenas para projetos de afinidade) */}
          {projectType === "affinity" && projectPartners && projectPartners.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Seleção de Parceiros por Nível</h4>
                <p className="text-sm text-muted-foreground">
                  Selecione os parceiros para cada parcela conforme os níveis do projeto
                </p>
              </div>

              {(() => {
                // Agrupar parceiros por nível
                const partnersByLevel = React.useMemo(() => {
                  const grouped: Record<number, typeof projectPartners> = {};
                  projectPartners.forEach((partner) => {
                    if (!grouped[partner.level_hierarchy]) {
                      grouped[partner.level_hierarchy] = [];
                    }
                    grouped[partner.level_hierarchy].push(partner);
                  });
                  return grouped;
                }, [projectPartners]);

                const levels = Object.keys(partnersByLevel)
                  .map(Number)
                  .sort((a, b) => a - b);

                return (
                  <div className="space-y-4">
                    {tempPlano.commission_distribution.map(
                      (_, installmentIndex: number) => (
                        <Card key={installmentIndex} className="p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="text-sm font-medium">
                              Parcela {installmentIndex + 1}º
                            </h5>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {levels.map((level) => {
                              const partnersAtLevel = partnersByLevel[level] || [];
                              const partnerOptions = partnersAtLevel.map((partner) => ({
                                value: partner.id,
                                label: `${partner.name} (${(partner.commission_percentage / 100).toFixed(2)}%)`,
                              }));

                              // Obter parceiro selecionado para este nível e parcela
                              const selectedPartnerId = (tempPlano as any).selectedPartners?.[installmentIndex]?.[level];

                              return (
                                <div key={`${installmentIndex}-${level}`} className="space-y-2">
                                  <FormLabel>Nível {level}</FormLabel>
                                  <Combobox
                                    options={partnerOptions}
                                    value={selectedPartnerId}
                                    onChange={(value) => {
                                      setTempPlano((prev) => {
                                        const current = prev as any;
                                        const selectedPartners = current.selectedPartners || {};
                                        if (!selectedPartners[installmentIndex]) {
                                          selectedPartners[installmentIndex] = {};
                                        }
                                        selectedPartners[installmentIndex][level] = value || undefined;
                                        return {
                                          ...prev,
                                          selectedPartners,
                                        };
                                      });
                                    }}
                                    placeholder={`Selecione parceiro do nível ${level}`}
                                    emptyMessage="Nenhum parceiro disponível"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Seleção de produtos */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Produtos do Plano</h4>
            <p className="text-sm text-muted-foreground">
              Selecione os produtos que farão parte deste plano
            </p>

            {(() => {
              const insuranceProducts = selectedProducts.filter(
                (p) => p.type !== EProductType.INSURANCE_ASSISTANCE
              );
              const assistanceProducts = selectedProducts.filter(
                (p) => p.type === EProductType.INSURANCE_ASSISTANCE
              );

              if (selectedProducts.length === 0) {
                return (
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-gray-500 py-8"
                        >
                          Nenhum produto selecionado na aba Produtos.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              }

              return (
                <div className="space-y-8">
                  {insuranceProducts.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Selecionar</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Capital</TableHead>
                          <TableHead>Prêmio</TableHead>
                          <TableHead>Descrição</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {insuranceProducts.map((p, i) => (
                          <TableRow key={`insurance-${i}`}>
                            <TableCell>
                              <Checkbox
                                checked={tempPlano.products.some(
                                  (product) => product.id === p.id
                                )}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    const productToAdd = {
                                      id: p.id,
                                      product_id: p.product_id,
                                      name: p.name,
                                      type: p.type,
                                      capital_value: p.capital_value,
                                      premium_value: p.premium_value,
                                      description: p.description,
                                      minimum_price: p.minimum_price,
                                      plan_name: p.plan_name,
                                    };
                                    setTempPlano((prev) => ({
                                      ...prev,
                                      products: [
                                        ...prev.products,
                                        productToAdd,
                                      ],
                                    }));
                                  } else {
                                    setTempPlano((prev) => ({
                                      ...prev,
                                      products: prev.products.filter(
                                        (product) => product.id !== p.id
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {p.plan_name || p.name || "Sem nome"}
                            </TableCell>
                            <TableCell>{t(`product.type.${p.type}`)}</TableCell>
                            <TableCell>
                              {formatPrice(p.capital_value)}
                            </TableCell>
                            <TableCell>
                              {formatPrice(p.premium_value)}
                            </TableCell>
                            <TableCell>{p.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {assistanceProducts.length > 0 && (
                    <>
                      <div className="border-t" />
                      <h5 className="text-sm font-medium">Assistências</h5>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Selecionar</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Preço mínimo</TableHead>
                            <TableHead>Valor de custo</TableHead>
                            <TableHead>Valor de venda</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead>
                              ID do Plano
                              <span className="text-red-500 ml-1">*</span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {assistanceProducts.map((p, i) => {
                            // Para assistências, garantir que temos um ID válido e consistente
                            // Se p.id não existir ou for undefined, usar product_id como fallback
                            const productId = p.id || p.product_id || `assistance-${i}`;
                            
                            return (
                            <TableRow key={`assistance-${i}-${productId}`}>
                              <TableCell>
                                <Checkbox
                                  checked={tempPlano.products.some(
                                    (product) => {
                                      // Comparar por id primeiro
                                      if (product.id === productId || product.id === p.id) {
                                        return true;
                                      }
                                      // Fallback: para assistências com capital ranges, comparar por product_id + description
                                      if (p.product_id && product.product_id && product.product_id === p.product_id) {
                                        if (p.description && product.description && product.description === p.description) {
                                          return true;
                                        }
                                        if (p.plan_name && product.plan_name && product.plan_name === p.plan_name) {
                                          return true;
                                        }
                                      }
                                      return false;
                                    }
                                  )}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      const productToAdd = {
                                        id: productId,
                                        product_id: p.product_id || p.id,
                                        name: p.name,
                                        type: p.type,
                                        // mapeando: preço mínimo = capital_value, valor de venda sempre começa zerado
                                        capital_value: p.capital_value,
                                        premium_value: 0, // Sempre começar zerado
                                        description: p.description,
                                        minimum_price: p.minimum_price,
                                        plan_name: p.plan_name,
                                        insurance_policy_extra_price_id:
                                          p.insurance_policy_extra_price_id,
                                      };
                                      setTempPlano((prev) => {
                                        // Verificar se já existe antes de adicionar
                                        const alreadyExists = prev.products.some((product) => {
                                          if (product.id === productId || product.id === p.id) {
                                            return true;
                                          }
                                          if (p.product_id && product.product_id && product.product_id === p.product_id) {
                                            if (p.description && product.description && product.description === p.description) {
                                              return true;
                                            }
                                            if (p.plan_name && product.plan_name && product.plan_name === p.plan_name) {
                                              return true;
                                            }
                                          }
                                          return false;
                                        });
                                        if (alreadyExists) {
                                          return prev;
                                        }
                                        return {
                                          ...prev,
                                          products: [
                                            ...prev.products,
                                            productToAdd,
                                          ],
                                        };
                                      });
                                    } else {
                                      setTempPlano((prev) => ({
                                        ...prev,
                                        products: prev.products.filter(
                                          (product) => {
                                            // Remover se o id corresponder
                                            if (product.id === productId || product.id === p.id) {
                                              return false;
                                            }
                                            // Fallback: para assistências com capital ranges
                                            if (p.product_id && product.product_id && product.product_id === p.product_id) {
                                              if (p.description && product.description && product.description === p.description) {
                                                return false;
                                              }
                                              if (p.plan_name && product.plan_name && product.plan_name === p.plan_name) {
                                                return false;
                                              }
                                            }
                                            return true;
                                          }
                                        ),
                                      }));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {p.plan_name || p.name || "Sem nome"}
                              </TableCell>
                              <TableCell>
                                {t(`product.type.${p.type}`)}
                              </TableCell>
                              <TableCell>
                                {formatPrice(
                                  p.minimum_price ?? p.capital_value
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-gray-600">
                                  {formatPrice(
                                    p.type === EProductType.INSURANCE_ASSISTANCE
                                      ? p.premium_value
                                      : 0
                                  )}
                                </span>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const productId = p.id || p.product_id || `assistance-${i}`;
                                  const selectedProduct = tempPlano.products.find((prod) => {
                                    if (prod.id === productId || prod.id === p.id) {
                                      return true;
                                    }
                                    if (p.product_id && prod.product_id && prod.product_id === p.product_id) {
                                      if (p.description && prod.description && prod.description === p.description) {
                                        return true;
                                      }
                                      if (p.plan_name && prod.plan_name && prod.plan_name === p.plan_name) {
                                        return true;
                                      }
                                    }
                                    return false;
                                  });
                                  const isSelected = Boolean(selectedProduct);
                                  if (!isSelected) {
                                    return formatPrice(0); // Sempre mostrar 0 quando não selecionado
                                  }
                                  return (
                                    <div className="max-w-[140px]">
                                      <RealCurrencyInput
                                        value={
                                          selectedProduct?.premium_value ?? 0
                                        }
                                        onChange={(value) => {
                                          setTempPlano((prev) => ({
                                            ...prev,
                                            products: prev.products.map(
                                              (prod) => {
                                                const matches = prod.id === productId || prod.id === p.id || 
                                                  (p.product_id && prod.product_id && prod.product_id === p.product_id && 
                                                   ((p.description && prod.description && prod.description === p.description) ||
                                                    (p.plan_name && prod.plan_name && prod.plan_name === p.plan_name)));
                                                return matches
                                                  ? {
                                                      ...prod,
                                                      premium_value: value,
                                                    }
                                                  : prod;
                                              }
                                            ),
                                          }));
                                        }}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>{p.description}</TableCell>
                              <TableCell>
                                {(() => {
                                  const productId = p.id || p.product_id || `assistance-${i}`;
                                  const selectedProduct = tempPlano.products.find((prod) => {
                                    if (prod.id === productId || prod.id === p.id) {
                                      return true;
                                    }
                                    if (p.product_id && prod.product_id && prod.product_id === p.product_id) {
                                      if (p.description && prod.description && prod.description === p.description) {
                                        return true;
                                      }
                                      if (p.plan_name && prod.plan_name && prod.plan_name === p.plan_name) {
                                        return true;
                                      }
                                    }
                                    return false;
                                  });
                                  const isSelected = Boolean(selectedProduct);
                                  if (!isSelected) {
                                    return ""; // Campo em branco se não estiver selecionado
                                  }
                                  const hasError = validationErrors.assistancePlanId && 
                                    (!selectedProduct?.insurance_policy_extra_price_id || 
                                     selectedProduct.insurance_policy_extra_price_id.trim() === "");
                                  return (
                                    <Input
                                      value={
                                        selectedProduct?.insurance_policy_extra_price_id ??
                                        ""
                                      }
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setTempPlano((prev) => ({
                                          ...prev,
                                          products: prev.products.map((prod) => {
                                            const matches = prod.id === productId || prod.id === p.id || 
                                              (p.product_id && prod.product_id && prod.product_id === p.product_id && 
                                               ((p.description && prod.description && prod.description === p.description) ||
                                                (p.plan_name && prod.plan_name && prod.plan_name === p.plan_name)));
                                            return matches
                                              ? {
                                                  ...prod,
                                                  insurance_policy_extra_price_id:
                                                    value,
                                                }
                                              : prod;
                                          }),
                                        }));
                                        // Limpar erro quando começar a digitar
                                        if (validationErrors.assistancePlanId && value.trim() !== "") {
                                          setValidationErrors((prev) => ({
                                            ...prev,
                                            assistancePlanId: undefined,
                                          }));
                                        }
                                      }}
                                      className={`h-8 text-sm ${
                                        hasError ? "border-red-500" : ""
                                      }`}
                                      placeholder="Informe o código"
                                      required
                                    />
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Mensagem de erro para ID do Plano */}
          {validationErrors.assistancePlanId && (
            <p className="text-sm text-red-500 text-center">
              {validationErrors.assistancePlanId}
            </p>
          )}

          {/* Botões de ação */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              {isEditing ? "Salvar Alterações" : "Salvar Plano"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
